import { HighlightsRenderer } from './highlights-renderer';
import { type GeometryResolver } from './types';

export interface HighlightsControllerOptions {
  id: string;
}

export class HighlightsController {
  #container: HTMLElement;

  #layers = new Set<HighlightsRenderer>();
  #resolver: GeometryResolver;

  constructor(resolver: GeometryResolver, options: HighlightsControllerOptions) {
    this.#resolver = resolver;

    this.#container = document.createElement('div');
    this.#container.id = options.id;

    this.#container.style.position = 'absolute';
    this.#container.style.top = '0';
    this.#container.style.left = '0';
    this.#container.style.pointerEvents = 'none';
  }

  /**
   * Attaches the highlights container to the scroll container.
   * MUST be called by the consumer after the DOM is ready.
   */
  public mount() {
    const parent = this.#resolver.getScrollContainer();

    if (parent) {
      if (this.#container.parentElement === parent) {
        return;
      }

      parent.style.position = 'relative';
      parent.appendChild(this.#container);
    }
  }

  /**
   * Factory: Creates a rendering layer with a specific z-index.
   */
  public createLayer(zIndex: number): HighlightsRenderer {
    const layer = new HighlightsRenderer(this.#container, this.#resolver, zIndex);

    this.#layers.add(layer);

    return layer;
  }

  /**
   * Safely destroys a specific layer and removes its DOM elements.
   */
  public removeLayer(layer: HighlightsRenderer) {
    if (this.#layers.has(layer)) {
      layer.dispose();
      this.#layers.delete(layer);
    }
  }

  /**
   * Completely tears down the controller, destroying all layers
   * and removing the root container from the DOM.
   */
  public dispose() {
    this.#layers.forEach((layer) => layer.dispose());
    this.#layers.clear();

    if (this.#container.parentElement) {
      this.#container.parentElement.removeChild(this.#container);
    }
  }

  /**
   * Returns the scroll container which is possibly null if the react app
   * has not mounted.
   */
  public getScrollContainer(): HTMLElement | null {
    return this.#resolver.getScrollContainer();
  }

  /**
   * Returns the highlights container.
   */
  public getContainer(): HTMLElement {
    return this.#container;
  }
}
